import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useReducer
} from "react";
import { useTranslation } from "utilities/i18n";
import { GroupDto, PolicyDeploymentScope, Site } from "generated/graphql";
import { NavLink, Route, Switch, useHistory } from "polyrouter";
import { Loader, PolyIcon } from "elements";
import DeviceSettingsLoader from "./ProvisionedDeviceSettings";
import { MAIN, SOFTWARE_UPDATE } from "Constants";
import "routes/Manage/Inventory/View/DeviceDetails.scss";
import { useDispatch, useSelector } from "react-redux";
import { State } from "store";
import BlockingForm from "collections/Form/BlockingForm";
import queryString from "query-string";
import { getUnsavedChangesCount } from "./MySettings";
import { resetChanges } from "store/settings";
import { AllQueryParam } from "polyrouter/allQueryParams";
import {
  getDeviceModel,
  isLines
} from "./utils/provisionedDeviceSettingsUtils";
import {
  NumOfLines,
  Setting,
  useProvisionedDeviceSettings
} from "./hooks/useProvisionedDeviceSettings";
import { createReducer, createStandardAction } from "typesafe-actions";
import { resetProvisionedDeviceSettingInheritance } from "store/provisionedDeviceSettingsInheritance";

export type SiteInfo = Pick<Site, "id" | "name">;
export type GroupInfo = Pick<GroupDto, "id" | "name">;

export type ProvisionedDevicePolicyScopeState = {
  policyScope: PolicyDeploymentScope;
};

export type SetProvisionedDevicePolicyScope = PolicyDeploymentScope;

const defaultProvisionedDevicePolicyScopeState = {
  policyScope: PolicyDeploymentScope.DeviceModel
};

export const setProvisionedDevicePolicyScope = createStandardAction(
  "SET_PROVISIONED_DEVICE_POLICY_SCOPE"
)<SetProvisionedDevicePolicyScope>();

export const ProvisionedDevicePolicyScopeReducer = createReducer(
  defaultProvisionedDevicePolicyScopeState
).handleAction(setProvisionedDevicePolicyScope, (state, action) => {
  return {
    ...state,
    policyScope: action.payload
  };
});

export const ProvisionedDevicePolicyScopeContext = createContext<
  [ProvisionedDevicePolicyScopeState, React.Dispatch<any>]
>([defaultProvisionedDevicePolicyScopeState, () => null]);

export const useProvisionedDevicePolicyScopeContext = () => {
  return useContext(ProvisionedDevicePolicyScopeContext);
};

export const ProvisionedDevicePolicyScopeProvider: React.FC = props => {
  const [state, dispatch] = useReducer(
    ProvisionedDevicePolicyScopeReducer,
    defaultProvisionedDevicePolicyScopeState
  );
  return (
    <ProvisionedDevicePolicyScopeContext.Provider
      value={[state, dispatch]}
      {...props}
    />
  );
};

type ProvisionedSettingsState = {
  maxConfiguredLine: number;
  externalPolicyLastSuccessfullyUpdated?: boolean | null;
  externalPolicyLastSuccessfullyUpdatedTime?: number | null;
};

const defaultProvisionedSettingsState = {
  maxConfiguredLine: 1,
  externalPolicyLastSuccessfullyUpdated: null,
  externalPolicyLastSuccessfullyUpdatedTime: null
};

export type SetProvisionedSettings = Partial<ProvisionedSettingsState>;

export const setProvisionedSettings = createStandardAction(
  "SET_PROVISIONED_SETTINGS"
)<SetProvisionedSettings>();

export const ProvisionedSettingsReducer = createReducer(
  defaultProvisionedSettingsState
).handleAction(setProvisionedSettings, (state, action) => {
  return {
    ...state,
    ...action.payload
  };
});

export const ProvisionedSettingsContext = React.createContext<
  [ProvisionedSettingsState, React.Dispatch<any>]
>([defaultProvisionedSettingsState, () => null]);

export const useProvisionedSettingsContext = () => {
  return useContext(ProvisionedSettingsContext);
};

export const ProvisionedSettingsProvider: React.FC = props => {
  const [state, dispatch] = useReducer(
    ProvisionedSettingsReducer,
    defaultProvisionedSettingsState
  );
  return (
    <ProvisionedSettingsContext.Provider value={[state, dispatch]} {...props} />
  );
};

const showLineWarning = (
  category: string,
  subcategory: string,
  collection: string,
  numOfLines: NumOfLines
) => {
  let showWarning = false;
  if (isLines(category, subcategory)) {
    const lineNumber = collection.match(/\d+/)?.map(Number)[0];
    if (lineNumber && lineNumber > numOfLines.regLimit) {
      showWarning = true;
    }
  }

  return showWarning;
};

export const SettingChangeIndicator: React.FC = () => (
  <span data-testid="setting-indicator" className="setting-indicator" />
);

export const ProvisionedSettingsNav: React.FC<{
  id?: string;
  model: string;
  siteId?: string;
  groupId?: string;
}> = ({ id, model, siteId, groupId }) => {
  const history = useHistory();
  const deviceModel = getDeviceModel(model);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [filter, setFilter] = useState("");
  const settings = useSelector((s: State) => s.settings);
  const [{ policyScope }] = useProvisionedDevicePolicyScopeContext();
  const { deviceSettings, numOfLines, fetching } = useProvisionedDeviceSettings(
    deviceModel,
    policyScope,
    id,
    siteId,
    groupId
  );

  useEffect(() => {
    // cleanup - reset settings changes when component is unmounted,
    // since we want to keep changes only for current settings session
    return () => {
      dispatch(resetChanges());
    };
  }, [dispatch]);

  const isSettingChanged = (
    category: string,
    subcategory: string,
    collection: string,
    setting: string
  ): boolean => !!settings[category]?.[subcategory]?.[collection]?.[setting];

  const isCollectionChanged = (
    category: string,
    subcategory: string,
    collection: string
  ) => !!settings[category]?.[subcategory]?.[collection];

  const isSubcategoryChanged = (category: string, subcategory: string) =>
    !!settings[category]?.[subcategory];

  const isCategoryChanged = (category: string) => !!settings[category];

  const changedSettingsCount: number = getUnsavedChangesCount(settings);
  const persistParams: AllQueryParam[] = [
    "PolicyFilterState",
    "deviceModel",
    "device",
    "site",
    "DeviceFilterState",
    "InsightsFilterState",
    "group",
    "previous"
  ];

  return (
    <Loader loading={fetching} className="text-left inline sm">
      <ul className="menu router sub scroll">
        <BlockingForm
          unsavedChanges={Object.keys(settings).length > 0}
          allowedSubRoutes={[
            "/settings",
            "/my-settings",
            "/manage/policy/details"
          ]}
        >
          <li className="filter mb-1 mt-1">
            <PolyIcon icon="filter" className="v-middle" />
            <input
              type={"text"}
              id={"filter-input"}
              className="form-control floating-input"
              placeholder={t("FILTER_SETTINGS")}
              value={filter ?? ""}
              onChange={e => setFilter(e.target.value)}
            />
          </li>
          <li>
            <NavLink
              nest
              to={{
                pathname: "/my-settings",
                search: history.location.search
              }}
            >
              {changedSettingsCount > 0 && <SettingChangeIndicator />}
              {t("MY_SETTINGS")}
            </NavLink>
          </li>
          <li className="divider mt-5" />
          {Object.keys(deviceSettings).map(category => (
            <li key={category + "-key"} className="active-transparent">
              <NavLink nest to={`/${category}`} persist={persistParams}>
                {isCategoryChanged(category) && <SettingChangeIndicator />}
                {category}
              </NavLink>
              {filter ? (
                <ul className="menu router sub pl-1">
                  {Object.keys(deviceSettings[category]).map(subcategory =>
                    Object.keys(deviceSettings[category][subcategory]).map(
                      collection =>
                        Object.values(
                          deviceSettings[category][subcategory][collection]
                        ).map(groupSettings => {
                          const settingsToFilter = Array.isArray(groupSettings)
                            ? groupSettings
                            : Object.values(groupSettings).flat();

                          return settingsToFilter
                            .filter((setting: Setting) => {
                              const settingName = setting.settingName.toLowerCase();
                              const friendlyName = setting.friendlyName?.toLowerCase();
                              const searchTerm = filter.toLowerCase();
                              return (
                                friendlyName?.includes(searchTerm) ||
                                settingName.includes(searchTerm)
                              );
                            })
                            .map((setting: Setting) => {
                              const name = setting.settingName;
                              return (
                                <li key={name + "-key"}>
                                  <NavLink
                                    nest
                                    to={{
                                      pathname: `/${category}/${subcategory}/${collection}`,
                                      search: `setting=${name}`
                                    }}
                                    persist={persistParams}
                                    isActive={(_, location) => {
                                      const { setting } = queryString.parse(
                                        location.search
                                      );
                                      return !!name && setting === name;
                                    }}
                                  >
                                    {isSettingChanged(
                                      category,
                                      subcategory,
                                      collection,
                                      name
                                    ) && <SettingChangeIndicator />}
                                    {setting.friendlyName || name}
                                  </NavLink>
                                </li>
                              );
                            });
                        })
                    )
                  )}
                </ul>
              ) : (
                <Route nest path={`/${category}`}>
                  <ul className="menu router sub pl-1">
                    {Object.keys(deviceSettings[category]).map(subcategory => (
                      <li key={subcategory + "-key"}>
                        <NavLink
                          nest
                          to={`/${subcategory}`}
                          persist={persistParams}
                        >
                          {isSubcategoryChanged(category, subcategory) && (
                            <SettingChangeIndicator />
                          )}
                          {subcategory}
                        </NavLink>
                        <Route nest path={`/${subcategory}`}>
                          <ul className="menu router sub pl-1">
                            {Object.keys(
                              deviceSettings[category][subcategory]
                            ).map(collection => {
                              if (collection === MAIN) return null;
                              const showWarning = showLineWarning(
                                category,
                                subcategory,
                                collection,
                                numOfLines
                              );

                              return (
                                <li key={collection + "-key"}>
                                  <NavLink
                                    nest
                                    to={{
                                      pathname: `/${collection}`,
                                      search: `warning=${showWarning}`
                                    }}
                                    persist={persistParams}
                                  >
                                    {isCollectionChanged(
                                      category,
                                      subcategory,
                                      collection
                                    ) && <SettingChangeIndicator />}
                                    {collection}
                                    {showWarning && (
                                      <PolyIcon
                                        className="float-right"
                                        icon="eye_slash"
                                      />
                                    )}
                                  </NavLink>
                                </li>
                              );
                            })}
                          </ul>
                        </Route>
                      </li>
                    ))}
                  </ul>
                </Route>
              )}
            </li>
          ))}
          <li>
            <NavLink nest exact to="/update" persist={persistParams}>
              {isCategoryChanged(SOFTWARE_UPDATE) && <SettingChangeIndicator />}
              {t("SOFTWARE_UPDATE")}
            </NavLink>
          </li>
        </BlockingForm>
      </ul>
    </Loader>
  );
};

export const ProvisionedDeviceSettingsRouter: React.FC<{
  model: string;
  id?: string;
  siteId?: string;
  siteName?: string;
  groupId?: string;
  groupName?: string;
  isPolicyView?: boolean;
}> = ({
  id,
  model,
  siteId,
  siteName,
  groupId,
  groupName,
  isPolicyView = false
}) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(resetProvisionedDeviceSettingInheritance());
  }, [dispatch]);

  return (
    <ProvisionedDevicePolicyScopeProvider>
      <ProvisionedSettingsProvider>
        <Switch>
          <Route nest path="/">
            <DeviceSettingsLoader
              model={model}
              deviceId={id}
              siteId={siteId}
              siteName={siteName}
              groupId={groupId}
              groupName={groupName}
              isPolicyView={isPolicyView}
            />
          </Route>
        </Switch>
      </ProvisionedSettingsProvider>
    </ProvisionedDevicePolicyScopeProvider>
  );
};
